import type { Notification } from '@cyphex/types/notifications';
import type { NotificationItem, NotificationKind } from './types';

/**
 * `cyphex.notification_kind` is snake_case on the wire and camelCase in the
 * client. An unknown member degrades to `system` rather than throwing — a new
 * server-side kind must never blank a user's Activity feed.
 */
const KIND_FROM_WIRE: Readonly<Record<string, NotificationKind>> = {
  message: 'message',
  mention: 'mention',
  reply: 'reply',
  reaction: 'reaction',
  channel_post: 'channelPost',
  announcement: 'announcement',
  call_incoming: 'callIncoming',
  call_missed: 'callMissed',
  voicemail: 'voicemail',
  meeting_invite: 'meetingInvite',
  meeting_reminder: 'meetingReminder',
  meeting_started: 'meetingStarted',
  meeting_changed: 'meetingChanged',
  file_shared: 'fileShared',
  file_comment: 'fileComment',
  task_assigned: 'taskAssigned',
  task_updated: 'taskUpdated',
  task_comment: 'taskComment',
  task_due: 'taskDue',
  approval_request: 'approvalRequest',
  team_invite: 'teamInvite',
  join_request: 'joinRequest',
  praise: 'praise',
  poll: 'poll',
  system: 'system',
  admin: 'admin',
};

export function kindFromWire(wire: string): NotificationKind {
  return KIND_FROM_WIRE[wire] ?? 'system';
}

/**
 * The wire preview names the scope by TYPE (channelName / teamName /
 * conversationTitle); the row renders one "scope" line, so they collapse here
 * rather than in three components.
 */
function toPreview(row: Notification): NotificationItem['preview'] {
  const preview = row.preview ?? {};
  const scopeName = preview.channelName ?? preview.teamName ?? preview.conversationTitle;
  return {
    ...(row.actor === null ? {} : { actorName: row.actor.displayName }),
    ...(row.actor?.avatarUrl == null ? {} : { actorAvatarUrl: row.actor.avatarUrl }),
    ...(scopeName === undefined ? {} : { scopeName }),
    ...(preview.snippet === undefined ? {} : { snippet: preview.snippet }),
    ...(preview.emoji === undefined ? {} : { emoji: preview.emoji }),
  };
}

/**
 * A MEETING'S TIME, IN THE READER'S OWN ZONE.
 *
 * The server writes an invitation's body as the day and hour in the zone the
 * MEETING is kept in - right for a push or an e-mail, which it renders itself,
 * and wrong on the screen of somebody three time zones away. The instant
 * travels in `preview.meetingTime`; this is the same sentence, said locally.
 * (An older invitation has no body at all, and gains one.)
 */
function bodyOf(row: Notification): string | null {
  const at = row.kind === 'meeting_invite' ? row.preview?.meetingTime : undefined;
  if (at === undefined) return row.body;
  const instant = new Date(at);
  if (Number.isNaN(instant.getTime())) return row.body;
  return instant.toLocaleString(undefined, {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    hour: 'numeric',
    minute: '2-digit',
  });
}

/** `Notification` (wire) → `NotificationItem` (client view model). */
export function toNotificationItem(row: Notification): NotificationItem {
  const body = bodyOf(row);
  return {
    id: row.id,
    kind: kindFromWire(row.kind),
    priority: row.priority,
    ...(row.actor === null ? {} : { actorId: row.actor.id }),
    ...(row.scopeKind === null ? {} : { scopeKind: row.scopeKind }),
    ...(row.scopeId === null ? {} : { scopeId: row.scopeId }),
    ...(row.targetKind === null ? {} : { targetKind: row.targetKind }),
    ...(row.targetId === null ? {} : { targetId: row.targetId }),
    ...(row.deepLink === null ? {} : { deepLink: row.deepLink }),
    title: row.title,
    ...(body === null ? {} : { body }),
    preview: toPreview(row),
    ...(row.groupKey == null ? {} : { groupKey: row.groupKey }),
    groupCount: row.groupCount,
    isRead: row.isRead,
    isSeen: row.isSeen,
    createdAt: Date.parse(row.createdAt),
  };
}
